import { Box, Typography, useMediaQuery } from "@material-ui/core";
import { useTheme } from "@material-ui/styles";
import React, { useContext, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { developOptions } from "../../../../config/developOptions";
import { THEME_NAMES } from "../../../../config/themes/config";
import { ConfigContext } from "../../../../contexts/ConfigContext";
import { ProcessContext } from "../../../../contexts/ProcessContext";
import { OptInsPatchCheckMapper } from "../../../../utils/mappers/OptInsPatchMappers";
import {
  checkInitialStateForDisableAcceptButton,
  getModuleOptIns,
  isAnyOptInInNullState,
} from "../../../../utils/optInsHelpers";
import MainButton from "../../../shared/components/MainButton";
import OptinsManager from "../../../shared/components/OptinsManager";
import Text from "../../../shared/components/Text";
import { ONBOARDING_FULFILLED_BY_TYPES } from "../../../shared/config";
import {
  OPTINS_TIMEOUT_CONFIG,
  REGISTRIES_TO_SKIP_TERMS,
  TERMS_MODULE_OPTINS_TYPES_LIST,
} from "../config";
import { CustomRegistry } from "@nemuru-eng/js-api-client";
import { TechnologicalPlatformTerms } from "./components/TechnologicalPlatformTerms";
import { AuthContext } from "../../../../contexts/AuthContext";

export default function Terms() {
  const { breakpoints } = useTheme();
  const isScreenXs = useMediaQuery(breakpoints.down("xs"));
  const { handleSubmit, getValues, errors, register, control, setValue } =
    useForm();
  const {
    state: {
      loan,
      optIns,
      lastActionsReferenceLastChange,
      lastActionsReference,
      participant,
    },
    dispatch,
  } = useContext(ProcessContext);

  const {
    state: { agent },
  } = useContext(AuthContext);

  const {
    state: { registry: customRegistry, language, themeName },
  } = useContext(ConfigContext);

  const [state, setState] = useState({
    optInsState: [],
    getOptinPollingTimeout: 0,
    disableAcceptButton: true,
    optInsBoxHeight: undefined,
    isOptInPatchFulfilled: undefined,
    showModule: false,
  });

  const {
    optInsState,
    disableAcceptButton,
    getOptinPollingTimeout,
    optInsBoxHeight,
    isOptInPatchFulfilled,
    showModule,
  } = state;
  const { palette } = useTheme();

  const isRegistryCaixaBankRelated =
    customRegistry === CustomRegistry.CAIXABANK ||
    customRegistry === CustomRegistry.TESLA ||
    customRegistry === CustomRegistry.FACILITEA;

  const optInsStateIsEmpty = optInsState?.length === 0;
  const { TIMEOUT_STEP_GET_OPTIN, MAX_TIMEOUT_GET_OPTIN } =
    OPTINS_TIMEOUT_CONFIG;

  const isOnboardingFulfilledByMerchantPOS =
    participant.onboardingFulfilledBy ===
    ONBOARDING_FULFILLED_BY_TYPES.MERCHANT_POS;

  const isActionTypeInLastActionsReference = (actionType) =>
    lastActionsReference?.some((element) => element.type === actionType);

  useEffect(() => {
    dispatchFlowLoading();
  }, []);

  useEffect(() => {
    if (!optIns) {
      dispatchGetOptinIfBorrowerIdExists();
    } else {
      checkWhichOptinsBelongToModuleAndSetState();
    }
  }, [optIns]);

  useEffect(() => {
    if (!developOptions.USE_FAKE_SERVICES_ON_POST_OR_PATCH) {
      setTimeout(() => {
        if (
          isActionTypeInLastActionsReference(
            "REQUEST_GET_OPTIN_BY_LOAN_ID_AND_BORROWER_ID_REJECTED"
          )
        ) {
          getOptinByLoanIdAndBorrowerIdWithPolling();
        }
      }, 1000);
    }
  }, [lastActionsReferenceLastChange]);

  useEffect(() => {
    !optInsStateIsEmpty && checkIfModuleMustBeShown();
    !optInsStateIsEmpty &&
      setState((s) => ({
        ...s,
        disableAcceptButton:
          checkInitialStateForDisableAcceptButton(optInsState),
      }));
  }, [optInsState]);

  useEffect(() => {
    !optInsStateIsEmpty &&
      showModule &&
      setState((s) => ({
        ...s,
        optInsBoxHeight:
          document.querySelector("#gdpr-box-optins").offsetHeight,
      }));
  }, [optInsStateIsEmpty, showModule]);

  useEffect(() => {
    if (!developOptions.USE_FAKE_SERVICES_ON_POST_OR_PATCH) {
      if (loan?.borrower_id && getOptinPollingTimeout < MAX_TIMEOUT_GET_OPTIN) {
        setTimeout(() => {
          if (
            isActionTypeInLastActionsReference(
              "REQUEST_GET_OPTIN_BY_LOAN_ID_AND_BORROWER_ID_REJECTED"
            )
          ) {
            getOptinByLoanIdAndBorrowerIdWithPolling();
          }
        }, 1000);
      }
    }
  }, [getOptinPollingTimeout]);

  useEffect(() => {
    if (
      isActionTypeInLastActionsReference(
        "REQUEST_PATCH_OPTIN_BY_ID_CONDITIONS_CHECK_IN_AND_SEQUENCE_FULFILLED"
      )
    ) {
      setState((s) => ({
        ...s,
        isOptInPatchFulfilled: true,
      }));
    }
    if (
      isActionTypeInLastActionsReference(
        "REQUEST_GET_OPTIN_BY_LOAN_ID_AND_BORROWER_ID_FULFILLED"
      )
    ) {
      if (isOptInPatchFulfilled) {
        dispatch({
          type: "SET_FLOW_LOADING",
          payload: { show: true },
        });
        dispatch({
          type: "SET_FLOW_ITEM_OK",
        });
      }
    }
  }, [lastActionsReferenceLastChange]);

  const dispatchFlowLoading = () => {
    dispatch({
      type: "SET_FLOW_LOADING",
      payload: {
        show: true,
        loadingMessage: <Text tId="PLEASE_WAIT" />,
      },
    });
  };

  const requestGetOptinByLoanIdAndBorrowerId = () => {
    dispatch({
      type: "REQUEST_GET_OPTIN_BY_LOAN_ID_AND_BORROWER_ID",
      payload: {
        loanId: loan.id,
        borrowerId: participant.participantId,
        language,
      },
    });
  };

  const dispatchGetOptinIfBorrowerIdExists = () => {
    loan && loan?.borrower_id && requestGetOptinByLoanIdAndBorrowerId();
  };

  const getOptinByLoanIdAndBorrowerIdWithPolling = () => {
    const POLLING_SET_STATE_TIMEOUT = 500;
    setTimeout(() => {
      requestGetOptinByLoanIdAndBorrowerId();
      setTimeout(() => {
        setState({
          ...state,
          getOptinPollingTimeout:
            getOptinPollingTimeout + TIMEOUT_STEP_GET_OPTIN,
        });
      }, POLLING_SET_STATE_TIMEOUT);
    }, TIMEOUT_STEP_GET_OPTIN);
  };

  const checkWhichOptinsBelongToModuleAndSetState = () => {
    const optInConditionsTempArray = getModuleOptIns(
      TERMS_MODULE_OPTINS_TYPES_LIST,
      optIns
    );
    setState((s) => ({
      ...s,
      optInsState: optInConditionsTempArray,
    }));
    dispatch({
      type: "SET_FLOW_LOADING",
      payload: { show: false },
    });
  };

  const checkIfModuleMustBeShown = () => {
    const skipModuleByRegistry = REGISTRIES_TO_SKIP_TERMS.some(
      (registry) => registry === customRegistry
    );

    const shallModuleBeShown =
      optInsState?.some((optin) => optin.required && !optin.checked) &&
      !skipModuleByRegistry;

    if (shallModuleBeShown) setState((s) => ({ ...s, showModule: true }));

    if (!shallModuleBeShown && !isOptInPatchFulfilled) {
      dispatch({
        type: "SET_MODULE_SKIPPED",
      });
    }
  };

  const handleChange = (name, value) => {
    setValue(name, value);
    setState((s) => ({
      ...s,
      disableAcceptButton: isAnyOptInInNullState(getValues()),
    }));
  };

  const acceptTerms = () => {
    submitConditionsConsents();
  };

  const submitConditionsConsents = () => {
    var conditions_check = [];
    var formValuesObject = getValues();

    //According to caixabank requirements, optIns must not be shown and must be forcibly accepted in order to proceed
    if (isRegistryCaixaBankRelated) {
      optInsState.map((optin) => {
        conditions_check.push({
          id: optin.condition_id,
          check: true,
        });
      });
    } else {
      Object.keys(formValuesObject).map((value) => {
        optInsState.map((optin) => {
          optin.name === value &&
            conditions_check.push({
              id: optin.condition_id,
              check: isRegistryCaixaBankRelated
                ? true
                : formValuesObject[value],
            });
        });
      });
    }

    const dataToSend = OptInsPatchCheckMapper.hydrate({
      id: optIns.id,
      conditions_check,
      loanId: loan.id,
      borrowerId: participant.participantId,
    });
    dispatch({
      type: "SET_FLOW_LOADING",
      payload: { show: true },
    });
    dispatch({
      type: "REQUEST_PATCH_OPTIN_BY_ID_CONDITIONS_CHECK_IN_AND_SEQUENCE",
      payload: dataToSend,
    });
  };

  if (!showModule) return <></>;

  return (
    !optInsStateIsEmpty && (
      <>
        <Box my={1} width="100%" textAlign="center" id="gdpr-box-welcome-title">
          <Typography
            variant={isScreenXs ? "subtitle1" : "h6"}
            style={{ color: palette.text.title }}
          >
            <strong>
              <Text tId={"DATA_TREATMENT"} />
            </strong>
          </Typography>
          <Box pt={1} className={"fade-bottom"}>
            <Box
              overflow="scroll"
              className="appear-anim"
              style={{
                paddingBottom: "1px",
                transition: "none",
                maxHeight: optInsBoxHeight
                  ? `calc(${
                      isScreenXs ? "80vh" : "63vh"
                    } - ${optInsBoxHeight}px)`
                  : 0,
              }}
            >
              <Box
                textAlign="justify"
                style={{
                  lineHeight: 0.5,
                  paddingBottom: "20px",
                }}
              >
                <Typography
                  component={"div"}
                  variant="caption"
                  style={{ fontSize: 13 }}
                >
                  {customRegistry === CustomRegistry.TECHNOLOGICAL_PLATFORM ? (
                    <TechnologicalPlatformTerms agentId={agent.id} />
                  ) : (
                    <Text tId={"TERMS_AND_CONDITIONS"} />
                  )}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
        <Box className="appear-anim" id="gdpr-box-optins">
          {!isRegistryCaixaBankRelated && (
            <>
              <Box pt={1} />
              {isOnboardingFulfilledByMerchantPOS && (
                <Box id="gdpr-box-welcome-subtitle" mb={1} display={"flex"}>
                  <Typography
                    align={"center"}
                    variant={"caption"}
                    color={"textPrimary"}
                  >
                    <Text tId={"PROVIDE_CONDITIONS_TO_CLIENT"} />
                  </Typography>
                </Box>
              )}
              <OptinsManager
                optInsState={optInsState}
                handleChange={handleChange}
                errors={errors}
                register={register}
                control={control}
              />
              {themeName === THEME_NAMES.upf && (
                <Typography
                  variant="caption"
                  color="textPrimary"
                  style={{
                    lineHeight: 1.5,
                    fontSize: 11,
                    wordWrap: "anywhere",
                  }}
                >
                  <Text tId={"UPF_GDPR_AND_PRIVACY_POLICY_OPTIN"} />
                </Typography>
              )}
            </>
          )}
          {isRegistryCaixaBankRelated ? (
            <MainButton onClick={() => acceptTerms()}>
              <Text tId="ACCEPT" />
            </MainButton>
          ) : (
            <MainButton
              disabled={disableAcceptButton}
              onClick={handleSubmit(acceptTerms)}
            >
              <Text tId="ACCEPT" />
            </MainButton>
          )}
        </Box>
      </>
    )
  );
}
